import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

export default function InflationCalculatorScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [amountToday, setAmountToday] = useState('100000');
  const [inflationRate, setInflationRate] = useState('6');
  const [years, setYears] = useState('10');
  const [expectedReturn, setExpectedReturn] = useState('10');

  const num = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return d;
    return n;
  };

  const data = useMemo(() => {
    const A = Math.max(0, num(amountToday, 0));
    const i = Math.max(0, num(inflationRate, 0)) / 100;
    const y = Math.max(0, num(years, 0));
    const r = Math.max(0, num(expectedReturn, 0)) / 100;
    const futureCost = A * Math.pow(1 + i, y);
    const cumulativeInflationPct = (Math.pow(1 + i, y) - 1) * 100;
    const futureSavings = A * Math.pow(1 + r, y);
    const todaysValueOfFutureSavings = futureSavings / Math.pow(1 + i, y);
    const realAnnualReturnPct = ((1 + r) / (1 + i) - 1) * 100;
    return { futureCost, cumulativeInflationPct, futureSavings, todaysValueOfFutureSavings, realAnnualReturnPct };
  }, [amountToday, inflationRate, years, expectedReturn]);

  const resetAll = () => {
    setAmountToday('100000');
    setInflationRate('6');
    setYears('10');
    setExpectedReturn('10');
  };

  const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>Inflation Calculator</Text>
          <Text style={styles.heroSubtitle}>Estimate future costs and real returns</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Inputs</Text>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Amount Today (₹)</Text>
                <TextInput value={amountToday} onChangeText={setAmountToday} style={styles.input} keyboardType="numeric" placeholder="e.g. 100000" />
              </View>
              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Inflation Rate (%)</Text>
                  <TextInput value={inflationRate} onChangeText={setInflationRate} style={styles.input} keyboardType="numeric" placeholder="e.g. 6" />
                </View>
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Years</Text>
                  <TextInput value={years} onChangeText={setYears} style={styles.input} keyboardType="numeric" placeholder="e.g. 10" />
                </View>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Investment Assumptions</Text>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Expected Return (%)</Text>
                <TextInput value={expectedReturn} onChangeText={setExpectedReturn} style={styles.input} keyboardType="numeric" placeholder="e.g. 10" />
              </View>
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Inflation Impact</Text>
                <Text style={styles.panelSubtitle}>How costs grow over time</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Future Cost</Text><Text style={styles.summaryValue}>{currency.format(data.futureCost)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Cumulative Inflation</Text><Text style={styles.summaryValue}>{formatNumber(data.cumulativeInflationPct, 2)}%</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Real Return</Text>
                <Text style={styles.panelSubtitle}>Purchasing power of savings</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Future Savings (Nominal)</Text><Text style={styles.summaryValue}>{currency.format(data.futureSavings)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Today's Value of Savings</Text><Text style={styles.summaryValue}>{currency.format(data.todaysValueOfFutureSavings)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Real Annual Return</Text><Text style={styles.summaryValue}>{formatNumber(data.realAnnualReturnPct, 2)}%</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderSEO(navigation)}
      {renderFAQ()}
      <Footer />
    </ScrollView>
  );
}

function renderSEO(navigation) {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>About Inflation Calculator</Text>
      <Text style={styles.seoText}>
        Inflation erodes purchasing power over time. This tool projects future costs and computes the real return of your savings by adjusting nominal returns for inflation, so you can assess what you truly gain.
      </Text>

      <Text style={styles.seoSubtitle}>Formulas used</Text>
      <Text style={styles.seoText}>
        Future Cost = Amount × (1 + inflation)^{years}. Real Return ≈ ((1 + nominal) / (1 + inflation) − 1). We also show today’s value of your future savings by discounting with inflation.
      </Text>

      <Text style={styles.seoSubtitle}>Interpreting results</Text>
      <Text style={styles.seoText}>
        A positive real return means your purchasing power increases; a negative value indicates that inflation outpaces your investment’s growth. Try different inflation and return assumptions to stress-test your plan.
      </Text>

      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
          <Text style={styles.linkText}>SIP Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('CAGRCalculator')}>
          <Text style={styles.linkText}>CAGR Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('APYCalculator')}>
          <Text style={styles.linkText}>APY Calculator</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.disclaimer}>
        Results are indicative and depend on the inputs. Actual inflation and returns vary. Please use realistic assumptions and consult official sources for current rates and rules.
      </Text>
    </View>
  );
}

function renderFAQ() {
  return (
    <View style={styles.faqContainer}>
      <Text style={styles.faqTitle}>Frequently Asked Questions</Text>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>What is real return?</Text>
        <Text style={styles.faqAnswer}>Real return adjusts your nominal investment return for inflation, indicating change in purchasing power.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Which inflation rate should I use?</Text>
        <Text style={styles.faqAnswer}>Use a rate that reflects your expected long-term inflation. You can try multiple scenarios to gauge sensitivity.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Can inflation be negative?</Text>
        <Text style={styles.faqAnswer}>Deflation can occur in some periods. Enter a negative inflation rate to test such scenarios, though it’s rare.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Why is my nominal return high but real return low?</Text>
        <Text style={styles.faqAnswer}>If inflation is close to or greater than your nominal return, purchasing power gains are limited or negative.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 24 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 18, paddingHorizontal: 16 },
  heroContent: { maxWidth: 1200, alignSelf: 'center' },
  heroTitle: { fontSize: 22, fontWeight: '800', color: '#ffffff', marginBottom: 4 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginVertical: 6 },
  input: { backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  label: { fontSize: 13, color: '#334155', marginBottom: 6 },
  flexItem: { flex: 1 },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: '#334155' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  buttonPrimary: { backgroundColor: '#4F46E5', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { color: '#ffffff', fontWeight: '600' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  seoSubtitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 6 },
  seoText: { color: '#334155', lineHeight: 20 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  linkButton: { backgroundColor: '#EEF2FF', borderColor: '#c7d2fe', borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  linkText: { color: '#1e293b', fontWeight: '600' },
  disclaimer: { color: '#64748b', fontSize: 12, marginTop: 10 },
  faqContainer: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  faqTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  faqItem: { marginBottom: 10 },
  faqQuestion: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
  faqAnswer: { color: '#334155', marginTop: 4, lineHeight: 20 },
});